import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Select, Space, Table, Tag, message } from 'antd';
import { DownloadOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { GET_PORTAL_BILLS } from 'graphql/queries/bills';
import { PagedPortalBill, PortalBill } from 'graphql/types';
import { usePermission } from 'components/PermissionProvider';
import { PORTAL_EXPORT_BILL } from 'graphql/mutations/bills';
import BillExportProgressModal from 'components/BillExportProgressModal';
import { getPortalBillStatusLabel } from 'utils/portalBillStatusLabel';

const { RangePicker } = DatePicker;

type BillStatusFilter = 'ALL' | 'PENDING_EXTERNAL_CONFIRM' | 'EXTERNAL_CONFIRMED' | 'EXTERNAL_REJECTED';

const getDefaultDateRange = (): [Dayjs, Dayjs] => [
    dayjs().subtract(29, 'day').startOf('day'),
    dayjs().endOf('day'),
];

const useRangePresets = () => {
    const { t } = useTranslation();
    return [
        { label: t('billList.filters.presets.last30Days'), value: getDefaultDateRange() as [Dayjs, Dayjs] },
        { label: t('billList.filters.presets.last90Days'), value: [dayjs().subtract(89, 'day').startOf('day'), dayjs().endOf('day')] as [Dayjs, Dayjs] },
        { label: t('billList.filters.presets.thisYear'), value: [dayjs().startOf('year'), dayjs().endOf('day')] as [Dayjs, Dayjs] },
    ];
};

const formatPeriod = (start: string, end: string, timeZone: string) => {
    if (!start || !end) return '-';
    const formatInstant = (value: string) => {
        try {
            return new Intl.DateTimeFormat('en-CA', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: timeZone || 'UTC',
            }).format(new Date(value));
        } catch {
            return dayjs(value).format('YYYY-MM-DD');
        }
    };
    return `${formatInstant(start)} ~ ${formatInstant(end)}`;
};

const statusColor = (status: string) => {
    switch (status) {
        case '4':
            return 'processing';
        case '7':
            return 'success';
        case '6':
            return 'error';
        default:
            return 'default';
    }
};

const BillListPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const presets = useRangePresets();
    const [billStatus, setBillStatus] = useState<BillStatusFilter>('ALL');
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(getDefaultDateRange());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [exportOpen, setExportOpen] = useState(false);
    const [exportTaskIds, setExportTaskIds] = useState<string[]>([]);

    const statusOptions = useMemo(
        () => [
            { value: 'ALL', label: t('billList.filters.status.all') },
            { value: 'PENDING_EXTERNAL_CONFIRM', label: t('billList.filters.status.pendingExternalConfirm') },
            { value: 'EXTERNAL_CONFIRMED', label: t('billList.filters.status.externalConfirmed') },
            { value: 'EXTERNAL_REJECTED', label: t('billList.filters.status.externalRejected') },
        ],
        [t],
    );

    const { data, loading, refetch } = useQuery<{ portalBills: PagedPortalBill }>(GET_PORTAL_BILLS, {
        variables: {
            billStatus: billStatus === 'ALL' ? 'ALL' : billStatus,
            startDate: dateRange[0]?.format('YYYY-MM-DD'),
            endDate: dateRange[1]?.format('YYYY-MM-DD'),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            page: page - 1,
            size: pageSize,
        },
    });

    const [exportBill, { loading: exporting }] = useMutation(PORTAL_EXPORT_BILL);

    const handleSearch = () => {
        setPage(1);
        refetch();
    };

    const handleExport = async (record: PortalBill) => {
        const result = await exportBill({
            variables: {
                billId: record.billId,
                template: record.exportTemplate || 'GENERAL_TEMPLATE',
                format: 'EXCEL',
                language: 'EN',
            },
        });
        if (result.data?.portalExportBill?.success) {
            const taskIds = result.data.portalExportBill.taskIds || [];
            if (taskIds.length === 0) {
                message.error(t('billDetail.exportFailed'));
                return;
            }
            setExportTaskIds(taskIds);
            setExportOpen(true);
        } else {
            message.error(result.data?.portalExportBill?.errorMessage || t('billDetail.exportFailed'));
        }
    };

    const columns = [
        {
            title: t('billList.columns.billId'),
            dataIndex: 'billId',
            key: 'billId',
            width: 120,
        },
        {
            title: t('billList.columns.billType'),
            dataIndex: 'billTypeLabel',
            key: 'billTypeLabel',
            width: 140,
        },
        {
            title: t('billList.columns.clientName'),
            dataIndex: 'clientName',
            key: 'clientName',
            width: 160,
            ellipsis: true,
        },
        {
            title: t('billList.columns.billStatus'),
            dataIndex: 'billStatusLabel',
            key: 'billStatusLabel',
            width: 200,
            render: (label: string, record: PortalBill) => (
                <Tag color={statusColor(record.billStatus)}>
                    {getPortalBillStatusLabel(record.billStatus, t, label)}
                </Tag>
            ),
        },
        {
            title: t('billList.columns.billAmount'),
            key: 'billAmount',
            width: 180,
            render: (_: unknown, record: PortalBill) => `${record.billAmount} ${record.currency}`,
        },
        {
            title: t('billList.columns.billTimeRange'),
            key: 'billTimeRange',
            width: 220,
            render: (_: unknown, record: PortalBill) =>
                formatPeriod(record.periodStart, record.periodEnd, record.timeZone),
        },
        {
            title: t('billList.columns.orderCount'),
            dataIndex: 'orderCount',
            key: 'orderCount',
            width: 100,
        },
        {
            title: t('billList.columns.timeZone'),
            dataIndex: 'timeZone',
            key: 'timeZone',
            width: 160,
            ellipsis: true,
        },
        {
            title: t('billList.columns.actions'),
            key: 'actions',
            fixed: 'right' as const,
            width: 220,
            render: (_: unknown, record: PortalBill) => (
                <Space size="small">
                    {hasPermission('portal:bill-list:view') && (
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/bill-detail/${record.billId}`)}
                        >
                            {t('billList.actions.checkBill')}
                        </Button>
                    )}
                    {hasPermission('portal:bill-list:export') && (
                        <Button
                            type="link"
                            size="small"
                            icon={<DownloadOutlined />}
                            loading={exporting}
                            onClick={() => handleExport(record)}
                        >
                            {t('billList.actions.exportDetails')}
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="bg-white rounded-lg p-6 shadow-sm">
            <h1 className="text-xl font-semibold mb-4">{t('billList.title')}</h1>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-gray-600">{t('billList.filters.billStatus')}</span>
                <Select
                    value={billStatus}
                    style={{ width: 220 }}
                    options={statusOptions}
                    onChange={(value: BillStatusFilter) => setBillStatus(value)}
                />
                <span className="text-sm text-gray-600">{t('billList.filters.billTimeRange')}</span>
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => setDateRange((dates ?? [null, null]) as [Dayjs | null, Dayjs | null])}
                    presets={presets}
                />
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                    {t('billList.filters.search')}
                </Button>
            </div>
            <Table<PortalBill>
                tableLayout="fixed"
                columns={columns}
                dataSource={data?.portalBills?.content}
                loading={loading}
                rowKey="billId"
                scroll={{ x: 1500 }}
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.portalBills?.totalElements,
                    onChange: (p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                    },
                    showSizeChanger: true,
                    showTotal: (total) => t('billList.pagination.total', { total }),
                }}
            />
            <BillExportProgressModal
                open={exportOpen}
                taskIds={exportTaskIds}
                onClose={() => setExportOpen(false)}
                onCompleted={() => message.success(t('billExport.completed'))}
            />
        </div>
    );
};

export default BillListPage;
